import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import HomePage from "./pages/HomePage";
import ToolsPage from "./pages/ToolsPage";
import ResourcesPage from "./pages/ResourcesPage";
import ToolDetailPage from "./pages/ToolDetailPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import MonthlySavingsPage from './pages/MonthlySavingsPage';
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";
import AboutPage from "./pages/AboutPage";
import TermsPage from "./pages/TermsPage";
import CookiesPolicyPage from "./pages/CookiesPolicyPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookieConsent from "./components/CookieConsent";
import ContactPage from "./pages/ContactPage";
import StructuredData from "./components/StructuredData";
import { createOrganizationSchema, createWebSiteSchema } from "./lib/schema-factory";
import { AuthProvider } from "./components/AuthProvider";
import { FloatingChatProvider } from "./components/FloatingChatContext";
import FloatingChatButton from "./components/FloatingChatButton";
import FloatingChatWindow from "./components/FloatingChatWindow";
import PricingPage from "./pages/PricingPage";
import ProfilePage from "./pages/ProfilePage";
import { EmailVerificationPage } from "./pages/EmailVerificationPage";
import { EmailVerifiedClosePage } from "./pages/EmailVerifiedClosePage";
import ChatPage from "./pages/ChatPage";
import { LanguageRedirect } from "./components/LanguageRedirect";
import { detectPreferredLanguage } from "@config/i18n.config";
import { Analytics } from "@vercel/analytics/react";

/**
 * Component that redirects root path to the user's preferred language
 * Detects language from: 1) localStorage, 2) browser settings, 3) default (en)
 */
function RootLanguageRedirect() {
  const preferredLang = detectPreferredLanguage();
  return <Navigate to={`/${preferredLang}`} replace />;
}

/**
 * Language-aware router that wraps all routes with /:lang prefix
 */
function LanguageRouter() {
  return (
    <>
      <ScrollToTop />
      <LanguageRedirect />
      <Routes>
        {/* Root redirect to user's preferred language (browser language or stored preference) */}
        <Route path="/" element={<RootLanguageRedirect />} />
        
        {/* Main pages */}
        <Route path="/:lang" element={<HomePage />} />
        <Route path="/:lang/chat" element={<ChatPage />} />
        <Route path="/:lang/tools" element={<ToolsPage />} />
        <Route path="/:lang/resources" element={<ResourcesPage />} />
        <Route path="/:lang/pricing" element={<PricingPage />} />
        <Route path="/:lang/about" element={<AboutPage />} />
        <Route path="/:lang/contact" element={<ContactPage />} />
        
        {/* Dynamic routes - Tools and Articles */}
        <Route path="/:lang/tools/:id" element={<ToolDetailPage />} />
        <Route path="/:lang/resources/:id" element={<ArticleDetailPage />} />
        
        {/* Legacy route - can be removed after migration */}
        <Route path="/:lang/tools/monthly-savings-calculator" element={<MonthlySavingsPage />} />
        
        {/* Legal pages */}
        <Route path="/:lang/terms" element={<TermsPage />} />
        <Route path="/:lang/cookies" element={<CookiesPolicyPage />} />
        <Route path="/:lang/privacy" element={<PrivacyPolicyPage />} />
        
        {/* User pages */}
        <Route path="/:lang/profile" element={<ProfilePage />} />
        <Route path="/:lang/verify-email" element={<EmailVerificationPage />} />
        <Route path="/:lang/email-verified" element={<EmailVerifiedClosePage />} />
        
        {/* Catch-all for paths without language prefix - redirect to user's preferred language */}
        <Route path="*" element={<RootLanguageRedirect />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <FloatingChatProvider>
        <TooltipProvider>
          {/* Global Structured Data - Organization and WebSite schemas */}
          <StructuredData schemas={[
            createOrganizationSchema(),
            createWebSiteSchema(),
          ]} />
          
          <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-grow">
              <LanguageRouter />
            </main>
            <Footer />
            <CookieConsent />
            <ScrollToTopButton />
            <FloatingChatButton />
            <FloatingChatWindow />
          </div>
          <Toaster />
          <Analytics />
        </TooltipProvider>
      </FloatingChatProvider>
    </AuthProvider>
  );
}

export default App;
